"use client";

import { FC } from "react";
import { Content } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import Image from "next/image";
import noise from "@/assets/noise.png";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { Button } from "@/components/ui/button";
import ctaLeftShape from "@/assets/common/ctaLeftShape.svg";
import ctaRightShape from "@/assets/common/ctaRightShape.svg";
import { motion } from "framer-motion";
import { MotionPrismicNextLink } from "@/components/ui/motion-prismic-next-link";
import GetInTouchCta from "@/app/(landing)/employability/components/get-in-touch-cta";
import FeesFundingCTA from "@/app/(landing)/fees-funding/components/fees-funding-cta";
import MentalHealthCta from "@/app/(landing)/mental-health/components/mental-health-cta";

/**
 * Props for `CallToActionCard`.
 */
export type CallToActionCardProps =
  SliceComponentProps<Content.CallToActionCardSlice>;

/**
 * Component for "CallToActionCard" Slices.
 */
const CallToActionCard: FC<CallToActionCardProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className=""
    >
      {slice.variation === "default" ? (
        <div className="bg-[#FAFDFF] py-[120px] relative">
          <div className="px-4">
            <Bounded
              as="div"
              className="container z-2 overflow-hidden relative flex mx-auto h-[340px] bg-[#045B91] rounded-4xl"
            >
              <div
                className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4]"
                style={{
                  background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
                }}
              />
              <Image
                src={ctaLeftShape.src}
                alt="left shape"
                width={701.78}
                height={648.95}
                
                className="absolute left-[-50%] sm:left-[-20%] md:left-0 top-0 h-[340px] w-auto"
              />
              <Image
                src={ctaRightShape.src}
                alt="right shape"
                width={710.89}
                height={657.36}
                className="absolute right-[-50%] sm:right-[-20%] md:right-0 bottom-0 h-[340px] w-auto"
              />
              <div className="flex flex-col relative flex-1 h-full items-center justify-center gap-6">
                <motion.h3
                  className="text-[#FAFDFF] text-center max-w-[580px] font-archivo text-4xl md:text-5xl font-semibold leading-12 md:leading-16"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 1 }}
                >
                  {slice.primary.title}
                </motion.h3>
                <MotionPrismicNextLink
                  field={slice.primary.cta_url}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.1, ease: "easeInOut" }}
                >
                  <Button className="rounded-[40px] h-[52px] bg-[linear-gradient(264deg,#0798F2_-29%,#43D9BD_85.45%)] hover:bg-[linear-gradient(264deg,#43D9BD_-29%,#0798F2_85.45%)] text-[#033D61] font-archivo text-base font-medium leading-6 px-6 py-3.5 cursor-pointer">
                    {slice.primary.cta_url.text}
                  </Button>
                </MotionPrismicNextLink>
              </div>
            </Bounded>
          </div>
          <div className="h-[250px] bg-[#EBF7FF] absolute bottom-0 w-full" />
        </div>
      ) : slice.variation === "getInTouchCta" ? (
        <Bounded className="md:py-20! py-10">
          <GetInTouchCta data={slice.primary} />
        </Bounded>
      ) : slice.variation === "feesAndFunding" ? (
        <Bounded className="py-20! xl:py-40! xl:px-10">
          <FeesFundingCTA data={slice.primary} />
        </Bounded>
      ) : slice.variation === "mentalWellbeingCta" ? (
        <div className="">
          <Bounded className="py-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1 }}
            >
              <MentalHealthCta data={slice.primary} />
            </motion.div>
          </Bounded>
        </div>
      ) : null}
    </section>
  );
};

export default CallToActionCard;
